/**
 * Centralny plik konfiguracyjny dla całej strony internetowej
 * Wszystkie ścieżki API, bazy danych i ustawienia w jednym miejscu
 * Obsługuje podzieloną architekturę: frontend (port 3000) + backend (port 8000)
 */

import fs from 'fs/promises';
import fsSync from 'fs';
import path from 'path';

// Typ dla konfiguracji strony
export interface SiteConfig {
  site: {
    name: string;
    domain: string;
    url: string;
    environment: string;
  };
  frontend: {
    url: string;
    port: number;
    protocol: string;
  };
  backend: {
    url: string;
    port: number;
    protocol: string;
    apiPath: string;
  };
  database: {
    host: string;
    user: string;
    password: string;
    name: string;
    port: number;
    charset: string;
    connectionLimit: number;
  };
  api: {
    baseUrl: string;
    externalBaseUrl: string;
    endpoints: {
      contactInfo: string;
      rodo: string;
      aboutUs: string;
      contact: string;
      courseRegistration: string;
      config: string;
      clearCache: string;
      courses: string;
      upcoming: string;
    };
    externalEndpoints: {
      contactInfo: string;
      rodo: string;
      aboutUs: string;
      courses: string;
      upcoming: string;
    };
    timeout: number;
    retries: number;
    useExternalApi: boolean;
  };
  admin: {
    path: string;
    loginPath: string;
    sessionLifetime: number;
    maxLoginAttempts: number;
    loginTimeout: number;
    baseUrl: string;
  };
  security: {
    csrfEnabled: boolean;
    sessionSecure: boolean;
    sessionHttpOnly: boolean;
    sessionSameSite: string;
    logActivity: boolean;
    cors: {
      enabled: boolean;
      allowedOrigins: string[];
      allowedMethods: string[];
      allowedHeaders: string[];
    };
    recaptcha: {
      siteKey: string;
      secretKey: string;
      version: string;
    };
  };
  cache: {
    enabled: boolean;
    ttl: number;
    headers: {
      cacheControl: string;
      pragma: string;
      expires: string;
    };
  };
  email: {
    smtp: {
      host: string;
      port: number;
      secure: boolean;
      user: string;
      password: string;
    };
    from: string;
    contactEmail: string;
    templates: {
      contactConfirmation: {
        subject: string;
        logoPath: string;
      };
      courseRegistration: {
        subject: string;
        logoPath: string;
      };
    };
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      text: string;
      background: string;
    };
    assets: {
      logoUrl: string;
      graduationCapIcon: string;
    };
    resourcesUrl?: {
      useBackendUrl: boolean;
      customUrl: string;
      logoPath: string;
      graduationCapPath: string;
    };
  };
  features: {
    useApiForContactInfo: boolean;
    useApiForRodo: boolean;
    useExternalApi: boolean;
    cacheEnabled: boolean;
    debugMode: boolean;
    maintenanceMode: boolean;
    developmentMode: boolean;
  };
  paths: {
    images: string;
    uploads: string;
    logs: string;
    backup: string;
  };
  lastUpdated: string;
  version: string;
}

/**
 * Pobiera wartość ze zmiennych środowiskowych z fallbackiem
 */
function getEnvValue(key: string, fallback: string = ''): string {
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || fallback;
  }
  return fallback;
}

function getEnvNumber(key: string, fallback: number): number {
  const value = getEnvValue(key, '');
  return value ? parseInt(value, 10) : fallback;
}

function getEnvBoolean(key: string, fallback: boolean): boolean {
  const value = getEnvValue(key, '');
  if (!value) return fallback;
  return value === 'true' || value === '1';
}

// Domyślna konfiguracja (fallback) - wrażliwe dane odczytywane ze zmiennych środowiskowych
const DEFAULT_CONFIG: SiteConfig = {
  site: {
    name: "Ośrodek Szkolenia Morskiego LIBRA",
    domain: "szkoleniamorskie.pl",
    url: getEnvValue('NEXT_PUBLIC_SITE_URL', "https://szkoleniamorskie.pl"),
    environment: getEnvValue('NODE_ENV', "production")
  },
  frontend: {
    url: getEnvValue('NEXT_PUBLIC_SITE_URL', "https://szkoleniamorskie.pl"),
    port: 3000,
    protocol: "https"
  },
  backend: {
    url: getEnvValue('NEXT_PUBLIC_SITE_URL', "https://szkoleniamorskie.pl"),
    port: 80,
    protocol: "https",
    apiPath: "/api"
  },
  database: {
    // Dane bazy ZAWSZE ze zmiennych środowiskowych - brak domyślnych haseł!
    host: getEnvValue('DB_HOST', "localhost"),
    user: getEnvValue('DB_USER', ""),
    password: getEnvValue('DB_PASSWORD', ""),
    name: getEnvValue('DB_NAME', ""),
    port: getEnvNumber('DB_PORT', 3306),
    charset: "utf8mb4",
    connectionLimit: getEnvNumber('DB_CONNECTION_LIMIT', 5)
  },
  api: {
    baseUrl: "/api",
    externalBaseUrl: "",
    endpoints: {
      contactInfo: "/api/contact-info",
      rodo: "/api/rodo-data",
      aboutUs: "/api/about-us",
      contact: "/api/contact",
      courseRegistration: "/api/course-registration",
      config: "/api/config",
      clearCache: "/api/clear-cache",
      courses: "/api/courses",
      upcoming: "/api/upcoming"
    },
    externalEndpoints: {
      contactInfo: "",
      rodo: "",
      aboutUs: "",
      courses: "",
      upcoming: ""
    },
    timeout: 30000,
    retries: 3,
    useExternalApi: false
  },
  admin: {
    path: "/paneladminalibra",
    loginPath: "/paneladminalibra/login",
    sessionLifetime: 604800,
    maxLoginAttempts: 999999,
    loginTimeout: 0,
    baseUrl: "https://szkoleniamorskie.pl/admin"
  },
  security: {
    csrfEnabled: false,
    sessionSecure: getEnvBoolean('SESSION_SECURE', true),
    sessionHttpOnly: getEnvBoolean('SESSION_HTTP_ONLY', true),
    sessionSameSite: getEnvValue('SESSION_SAME_SITE', "Lax"),
    logActivity: false,
    cors: {
      enabled: true,
      allowedOrigins: ["https://szkoleniamorskie.pl", "https://www.szkoleniamorskie.pl", "http://szkoleniamorskie.pl", "http://www.szkoleniamorskie.pl"],
      allowedMethods: ["GET", "POST", "PUT", "DELETE", "OPTIONS"],
      allowedHeaders: ["Content-Type", "Authorization", "X-Requested-With"]
    },
    recaptcha: {
      // reCAPTCHA klucze ze zmiennych środowiskowych
      siteKey: getEnvValue('NEXT_PUBLIC_RECAPTCHA_SITE_KEY', ""),
      secretKey: getEnvValue('RECAPTCHA_SECRET_KEY', ""),
      version: "v2"
    }
  },
  cache: {
    enabled: true,
    ttl: 3600,
    headers: {
      cacheControl: "public, max-age=3600, s-maxage=3600",
      pragma: "cache",
      expires: "3600"
    }
  },
  email: {
    smtp: {
      // SMTP ze zmiennych środowiskowych - brak zahardkodowanych haseł!
      host: getEnvValue('SMTP_HOST', ""),
      port: getEnvNumber('SMTP_PORT', 465),
      secure: getEnvBoolean('SMTP_SECURE', true),
      user: getEnvValue('SMTP_USER', ""),
      password: getEnvValue('SMTP_PASSWORD', "")
    },
    from: getEnvValue('EMAIL_FROM', ""),
    contactEmail: getEnvValue('EMAIL_CONTACT', "info@szkoleniamorskie.pl"),
    templates: {
      contactConfirmation: {
        subject: "Potwierdzenie wysłania formularza kontaktowego - Ośrodek Szkolenia Morskiego LIBRA",
        logoPath: "/images/logo.webp"
      },
      courseRegistration: {
        subject: "Potwierdzenie zapisu na kurs - Ośrodek Szkolenia Morskiego LIBRA",
        logoPath: "/images/logo.webp"
      }
    },
    colors: {
      primary: "rgb(6, 40, 95)",
      secondary: "#ffffff",
      accent: "#e63946",
      text: "#333333",
      background: "#f8f9fa"
    },
    assets: {
      logoUrl: "/images/logoczerwone.webp",
      graduationCapIcon: "/images/graduation-cap-white.png"
    }
  },
  features: {
    useApiForContactInfo: true,
    useApiForRodo: true,
    useExternalApi: getEnvBoolean('USE_EXTERNAL_API', false),
    cacheEnabled: getEnvBoolean('CACHE_ENABLED', true),
    debugMode: getEnvBoolean('DEBUG_MODE', false),
    maintenanceMode: false,
    developmentMode: getEnvBoolean('DEVELOPMENT_MODE', getEnvValue('NODE_ENV', 'production') !== 'production')
  },
  paths: {
    images: "/images",
    uploads: "/uploads",
    logs: "/logs",
    backup: "/backup"
  },
  lastUpdated: "2024-12-19T12:00:00.000Z",
  version: "1.0.0"
};

// Cache dla konfiguracji
let cachedConfig: SiteConfig | null = null;

/**
 * Ładuje konfigurację z pliku JSON i nadpisuje URLami ze zmiennych środowiskowych w produkcji
 */
async function loadSiteConfig(): Promise<SiteConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  try {
    const response = await fetch('/config/site-config.json?t=' + Date.now(), {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    let config: SiteConfig;
    if (!response.ok) {
      config = { ...DEFAULT_CONFIG };
    } else {
      const jsonConfig = await response.json();
      config = { ...DEFAULT_CONFIG, ...jsonConfig };
    }

    // W produkcji nadpisz URLe ze zmiennych środowiskowych
    if (typeof window !== 'undefined') {
      // Po stronie klienta sprawdź czy mamy zmienne środowiskowe w window
      const hostName = window.location.hostname;
      const protocol = window.location.protocol;
      const isProduction = !hostName.includes('localhost') && hostName !== '127.0.0.1';
      
      if (isProduction) {
        const baseUrl = `${protocol}//${hostName}`;
        
        // Nadpisz konfigurację dla produkcji
        config.frontend.url = baseUrl;
        config.backend.url = baseUrl;
        config.admin.baseUrl = `${baseUrl}/admin`;
        config.api.externalBaseUrl = `${baseUrl}/admin/api`;
        config.site.url = baseUrl;
        config.site.environment = 'production';
        config.features.developmentMode = false;
        
        // Dodaj obecną domenę do CORS
        if (!config.security.cors.allowedOrigins.includes(baseUrl)) {
          config.security.cors.allowedOrigins.push(baseUrl);
        }
      }
    } else if (process.env.NODE_ENV === 'production') {
      // Po stronie serwera w produkcji
      const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || config.site.url;
      
      config.frontend.url = baseUrl;
      config.backend.url = baseUrl;
      config.admin.baseUrl = `${baseUrl}/admin`;
      config.api.externalBaseUrl = `${baseUrl}/admin/api`;
      config.site.url = baseUrl;
      config.site.environment = 'production';
      config.features.developmentMode = false;
    }

    cachedConfig = config;
    return cachedConfig;
  } catch (error) {
    console.error('Błąd podczas ładowania konfiguracji:', error);
    cachedConfig = DEFAULT_CONFIG;
  }

  return cachedConfig;
}

/**
 * Pobiera konfigurację strony (synchronicznie z cache lub asynchronicznie)
 */
export function getSiteConfig(): SiteConfig {
  if (cachedConfig !== null) {
    return cachedConfig;
  }

  // Po stronie serwera - spróbuj załadować JSON synchronicznie
  if (typeof window === 'undefined') {
    try {
      // Znajdź ścieżkę do pliku konfiguracyjnego
      const possiblePaths = [
        path.join(process.cwd(), 'public', 'config', 'site-config.json'),
        path.join(process.cwd(), '.next', 'static', 'config', 'site-config.json'),
        path.resolve('./public/config/site-config.json')
      ];

      for (const configPath of possiblePaths) {
        try {
          if (fsSync.existsSync(configPath)) {
            const fileContent = fsSync.readFileSync(configPath, 'utf-8');
            const jsonConfig = JSON.parse(fileContent);

            // Połącz z DEFAULT_CONFIG (zmienne środowiskowe mają priorytet)
            let config = deepMerge(DEFAULT_CONFIG, jsonConfig);

            // Nadpisz wrażliwe dane ze zmiennych środowiskowych jeśli są ustawione
            if (process.env.DB_HOST) config.database.host = process.env.DB_HOST;
            if (process.env.DB_USER) config.database.user = process.env.DB_USER;
            if (process.env.DB_PASSWORD !== undefined) config.database.password = process.env.DB_PASSWORD;
            if (process.env.DB_NAME) config.database.name = process.env.DB_NAME;
            if (process.env.SMTP_HOST) config.email.smtp.host = process.env.SMTP_HOST;
            if (process.env.SMTP_USER) config.email.smtp.user = process.env.SMTP_USER;
            if (process.env.SMTP_PASSWORD) config.email.smtp.password = process.env.SMTP_PASSWORD;
            if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) config.security.recaptcha.siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
            if (process.env.RECAPTCHA_SECRET_KEY) config.security.recaptcha.secretKey = process.env.RECAPTCHA_SECRET_KEY;

            // W produkcji dostosuj URLe
            if (process.env.NODE_ENV === 'production') {
              const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || config.site.url;
              config.frontend.url = baseUrl;
              config.backend.url = baseUrl;
              config.admin.baseUrl = `${baseUrl}/admin`;
              config.api.externalBaseUrl = `${baseUrl}/admin/api`;
              config.site.url = baseUrl;
              config.site.environment = 'production';
              config.features.developmentMode = false;
            }

            cachedConfig = config;
            return config;
          }
        } catch (readError) {
          // Kontynuuj do następnej ścieżki
        }
      }
    } catch (error) {
      console.error('Błąd podczas synchronicznego ładowania konfiguracji:', error);
    }
  }

  // Fallback - zwróć konfigurację z dynamicznym wykrywaniem środowiska
  let config = { ...DEFAULT_CONFIG };

  if (typeof window !== 'undefined') {
    // Po stronie klienta
    const hostName = window.location.hostname;
    const protocol = window.location.protocol;
    const isProduction = !hostName.includes('localhost') && hostName !== '127.0.0.1';

    if (isProduction) {
      const baseUrl = `${protocol}//${hostName}`;

      config.frontend.url = baseUrl;
      config.backend.url = baseUrl;
      config.admin.baseUrl = `${baseUrl}/admin`;
      config.api.externalBaseUrl = `${baseUrl}/admin/api`;
      config.site.url = baseUrl;
      config.site.environment = 'production';
      config.features.developmentMode = false;

      // Dodaj obecną domenę do CORS
      if (!config.security.cors.allowedOrigins.includes(baseUrl)) {
        config.security.cors.allowedOrigins.push(baseUrl);
      }
    }
  } else if (process.env.NODE_ENV === 'production') {
    // Po stronie serwera w produkcji
    const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || config.site.url;

    config.frontend.url = baseUrl;
    config.backend.url = baseUrl;
    config.admin.baseUrl = `${baseUrl}/admin`;
    config.api.externalBaseUrl = `${baseUrl}/admin/api`;
    config.site.url = baseUrl;
    config.site.environment = 'production';
    config.features.developmentMode = false;
  }

  return config;
}

/**
 * Głębokie scalanie obiektów (helper)
 */
function deepMerge(target: any, source: any): any {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
}

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Pobiera konfigurację strony z pliku JSON (asynchronicznie)
 */
export async function getSiteConfigAsync(): Promise<SiteConfig> {
  return await loadSiteConfig();
}

/**
 * Czyści cache konfiguracji
 */
export function clearConfigCache(): void {
  cachedConfig = null;
}

/**
 * Pobiera URL endpointu API (wewnętrznego lub zewnętrznego)
 */
export function getApiUrl(endpoint: keyof SiteConfig['api']['endpoints']): string {
  const config = getSiteConfig();
  
  // Jeśli używamy zewnętrznego API i endpoint istnieje w externalEndpoints
  if (config.api.useExternalApi && endpoint in config.api.externalEndpoints) {
    const externalEndpoint = config.api.externalEndpoints[endpoint as keyof typeof config.api.externalEndpoints];
    return `${config.backend.url}${externalEndpoint}`;
  }
  
  // Domyślnie używaj wewnętrznych endpointów
  return config.api.endpoints[endpoint];
}

/**
 * Pobiera URL zewnętrznego endpointu API (backend)
 */
export function getExternalApiUrl(endpoint: keyof SiteConfig['api']['externalEndpoints']): string {
  const config = getSiteConfig();
  return `${config.backend.url}${config.api.externalEndpoints[endpoint]}`;
}

/**
 * Pobiera URL wewnętrznego endpointu API (frontend)
 */
export function getInternalApiUrl(endpoint: keyof SiteConfig['api']['endpoints']): string {
  const config = getSiteConfig();
  return config.api.endpoints[endpoint];
}

/**
 * Pobiera nagłówki cache
 */
export function getCacheHeaders(): Record<string, string> {
  const config = getSiteConfig();
  return {
    'Cache-Control': config.cache.headers.cacheControl,
    'Pragma': config.cache.headers.pragma,
    'Expires': config.cache.headers.expires
  };
}

/**
 * Pobiera nagłówki CORS dla zewnętrznych żądań
 */
export function getCorsHeaders(): Record<string, string> {
  const config = getSiteConfig();
  
  if (!config.security.cors.enabled) {
    return {};
  }
  
  return {
    'Access-Control-Allow-Origin': config.security.cors.allowedOrigins.join(', '),
    'Access-Control-Allow-Methods': config.security.cors.allowedMethods.join(', '),
    'Access-Control-Allow-Headers': config.security.cors.allowedHeaders.join(', ')
  };
}

/**
 * Sprawdza czy funkcja jest włączona
 */
export function isFeatureEnabled(feature: keyof SiteConfig['features']): boolean {
  const config = getSiteConfig();
  return config.features[feature];
}

/**
 * Sprawdza czy używamy zewnętrznego API
 */
export function useExternalApi(): boolean {
  return isFeatureEnabled('useExternalApi');
}

/**
 * Sprawdza czy jesteśmy w trybie deweloperskim
 */
export function isDevelopmentMode(): boolean {
  return isFeatureEnabled('developmentMode');
}

/**
 * Pobiera URL backendu
 */
export function getBackendUrl(): string {
  const config = getSiteConfig();
  return config.backend.url;
}

/**
 * Pobiera URL frontendu
 */
export function getFrontendUrl(): string {
  const config = getSiteConfig();
  return config.frontend.url;
}

/**
 * Pobiera URL panelu administracyjnego
 */
export function getAdminUrl(): string {
  const config = getSiteConfig();
  return config.admin.baseUrl;
}

// Inicjalizacja konfiguracji przy pierwszym imporcie
if (typeof window !== 'undefined') {
  // Wyczyść cache jeśli zmieniono domenę
  try {
    const currentHost = window.location.hostname;
    const cachedHost = localStorage.getItem('libra_cached_host');

    if (cachedHost && cachedHost !== currentHost) {
      clearConfigCache();
      localStorage.setItem('libra_cached_host', currentHost);
    } else if (!cachedHost) {
      localStorage.setItem('libra_cached_host', currentHost);
    }
  } catch (e) { /* private browsing */ }

  loadSiteConfig().catch(console.error);
}

export default getSiteConfig; 
